/**
 * PaymentResult.jsx — PUBLIC Moka ödeme sonuç sayfası.
 *
 * Route: /odeme-sonuc?durum=tamamlandi|bilinmeyen|dogrulanamadi|kapali|basarisiz&taksit=<id>
 * Auth: GEREKMEZ — App.js'te ProtectedRoute/RoleRoute'a SARILMAZ, standalone
 * iskelet AccountDeletionPage.jsx/LegalPage.jsx ile AYNI desen (global Navbar
 * gizlenir — bkz. App.js).
 *
 * Backend hedefi: backend/routes/moka_links.py::moka_payment_callback —
 * Moka'nın `RedirectUrl`'i, öğrencinin tarayıcısı ödeme sonrası buraya düşer.
 *
 * ÖNEMLİ (Recep kararı): `durum=tamamlandi` bile tahsilatın kesin yazıldığı
 * anlamına GELMEZ — callback yalnızca "hash geçerli, Moka API'sinden doğrulama
 * TETİKLENDİ" demektir; gerçek yazım `process_moka_payment`/polling job'ın
 * sonucuna bağlıdır (bkz. backend docstring'i). Bu yüzden metin kesin dil
 * KULLANMAZ ("ödemeniz alındı, kaydınız kısa süre içinde güncellenecek" gibi).
 * URL'deki `taksit` id'si BİLEREK ekrana basılmaz (iç referans, öğrenciye
 * anlamsız + gereksiz bilgi sızıntısı).
 */
import { useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, HelpCircle, ShieldAlert, PowerOff, XCircle, ArrowLeft, LogIn } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import useReducedMotion from '../hooks/useReducedMotion';
import Logo from '../components/Logo';

const VALID_LANGS = ['tr', 'en'];

const STATUS_MAP = {
  tamamlandi: { icon: CheckCircle2, color: 'emerald', key: 'completed' },
  bilinmeyen: { icon: HelpCircle, color: 'amber', key: 'unknown' },
  dogrulanamadi: { icon: ShieldAlert, color: 'rose', key: 'dogrulanamadi' },
  kapali: { icon: PowerOff, color: 'slate', key: 'kapali' },
  basarisiz: { icon: XCircle, color: 'rose', key: 'basarisiz' },
};

const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
};

export default function PaymentResult() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, toggleLanguage, t } = useLanguage();
  const reduced = useReducedMotion();
  const appliedInitialOverride = useRef(false);

  const durum = searchParams.get('durum') || '';
  // taksit id KASITLI olarak okunmaz/ekranda gösterilmez — bkz. dosya üstü not.
  const status = STATUS_MAP[durum] || null;
  const statusKey = status?.key || 'fallback';
  const colors = COLOR_CLASSES[status?.color || 'slate'];
  const Icon = status?.icon || HelpCircle;

  useEffect(() => {
    if (appliedInitialOverride.current) return;
    appliedInitialOverride.current = true;
    const langParam = searchParams.get('lang');
    if (VALID_LANGS.includes(langParam) && langParam !== lang) {
      toggleLanguage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = t('paymentResult.meta.title');
    return () => { document.title = 'Kariyer Online'; };
  }, [t]);

  const switchLang = (nextLang) => {
    if (nextLang !== lang) toggleLanguage();
    const next = new URLSearchParams(searchParams);
    next.set('lang', nextLang);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen duo-bg flex flex-col" data-testid="payment-result-page">
      <header className="border-b-2 border-[var(--duo-stroke)] px-4 sm:px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex" data-testid="payment-result-logo-home-link">
            <Logo size={28} />
          </Link>
          <div className="flex items-center gap-1" role="group" aria-label={t('legal.langToggle.label')}>
            {VALID_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => switchLang(l)}
                data-testid={`payment-result-lang-toggle-${l}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                  lang === l ? 'bg-[var(--duo-blue)] text-white' : 'text-[var(--duo-text-soft)] hover:bg-[var(--duo-sky)]'
                }`}
              >
                {t(`legal.langToggle.${l}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center px-4 sm:px-6 py-10">
        <motion.div
          className="max-w-lg mx-auto w-full bg-white rounded-2xl p-6 sm:p-10 text-center"
          style={{ border: '2px solid var(--duo-stroke)' }}
          initial={reduced ? false : { opacity: 0, y: 10 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className={`w-16 h-16 rounded-2xl ${colors.bg} ${colors.text} flex items-center justify-center mx-auto mb-4`}>
            <Icon size={32} />
          </div>
          <h1 className="font-duo-display text-2xl sm:text-3xl text-[var(--duo-text)] mb-3" data-testid="payment-result-title">
            {t(`paymentResult.${statusKey}.title`)}
          </h1>
          <p className="text-sm text-[var(--duo-text)] font-medium mb-8" data-testid="payment-result-body">
            {t(`paymentResult.${statusKey}.body`)}
          </p>

          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/" className="text-sm font-bold text-[var(--duo-blue)] hover:underline inline-flex items-center gap-1.5" data-testid="payment-result-back-home">
              <ArrowLeft size={14} />
              {t('paymentResult.backHome')}
            </Link>
            <Link to="/login" className="text-sm font-bold text-[var(--duo-blue)] hover:underline inline-flex items-center gap-1.5" data-testid="payment-result-back-login">
              <LogIn size={14} />
              {t('paymentResult.backLogin')}
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
